import { ParsedData, StepProps, useAutoNavigate, useFlowState } from '@betterment/js-runtime';
import { Box, H1, Text } from '@betterment/samba-react';
import { Button, FormikTextInput } from '@betterment/style-closet-react';
import { partial as _partial } from 'es-toolkit/compat';
import { Form, Formik, FormikHelpers } from 'formik';

import { PinResetParams } from '../Checking/data';
import { InputGroupWithLabelAndError, applyApiErrorsToFormik } from '../Checking/form-helpers';
import { validateConfirmPin, validateNewPin } from '../Checking/pin-validations';
import FlashErrorMessage from '../Shared/components/FlashErrorMessage';
import { WorkflowLayout } from '../Shared/layout/WorkflowLayout';

const initialValues = {
  newPin: '',
  confirmPin: '',
};

export interface Props extends StepProps {
  onPinReset: (values: PinResetParams) => Promise<ParsedData<{}>>;
  goToNextStep: () => void;
}

const PinChangeModal: React.FC<React.PropsWithChildren<Props>> = ({ onPinReset, goToNextStep }) => {
  const autoNavigate = useAutoNavigate();
  const { flowState } = useFlowState();

  const onSubmit = async (values: PinResetParams, formik: FormikHelpers<typeof initialValues>) => {
    try {
      const response: ParsedData<{}> = await onPinReset(values);

      if (response && response.errors) {
        applyApiErrorsToFormik(formik, response.errors);
      } else {
        goToNextStep();
      }
    } finally {
      formik.setSubmitting(false);
    }
  };

  return (
    <WorkflowLayout
      pageTitle="Change PIN"
      workflowTitle="Change PIN"
      size="large"
      onClose={() => autoNavigate(flowState.returnToPath)}
    >
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: { base: '1fr', md: '1fr 1fr' },
          gap: 'space.500',
          width: '100%',
          minWidth: 'container.large',
        }}
      >
        <Box>
          <H1 css={{ marginBottom: 'space.300' }}>Change your PIN</H1>
          <Text
            as="p"
            css={{
              lineHeight: 'lineHeight.100',
              marginBottom: 'space.400',
            }}
          >
            It may take up to an hour after setting your PIN for it to take effect. If you ever forget your PIN, you'll
            be able to reset it by logging into your account.
          </Text>
        </Box>
        <Box>
          <Formik<typeof initialValues>
            onSubmit={onSubmit}
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
          >
            {({ isSubmitting, values, validateField, setTouched, touched }) => {
              return (
                <Form>
                  <FlashErrorMessage name="base" />
                  <InputGroupWithLabelAndError fieldName="newPin" labelText="Enter new PIN">
                    {(props: { id: string; name: string }) => (
                      <FormikTextInput
                        {...props}
                        disabled={isSubmitting}
                        type="password"
                        maxLength={4}
                        mb="3"
                        validate={validateNewPin}
                        onBlur={() => {
                          void setTouched({ ...touched, newPin: true });
                          void validateField('newPin');
                        }}
                      />
                    )}
                  </InputGroupWithLabelAndError>
                  <InputGroupWithLabelAndError fieldName="confirmPin" labelText="Confirm new PIN">
                    {(props: { id: string; name: string }) => (
                      <FormikTextInput
                        {...props}
                        disabled={isSubmitting}
                        type="password"
                        maxLength={4}
                        mb="3"
                        validate={_partial(validateConfirmPin, values.newPin)}
                        onBlur={() => {
                          void setTouched({ ...touched, confirmPin: true });
                          void validateField('confirmPin');
                        }}
                      />
                    )}
                  </InputGroupWithLabelAndError>
                  <Button type="submit" px="10" disabled={isSubmitting}>
                    Update PIN
                  </Button>
                </Form>
              );
            }}
          </Formik>
        </Box>
      </Box>
    </WorkflowLayout>
  );
};

export default PinChangeModal;
